import { isEqual } from 'lodash';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';

import { Actions } from '~/eds/src/components/Actions/Actions';
import { CardsGrid } from '~/eds/src/components/Card/subcomponents/CardsGrid';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { FullPageOverlay } from '~/eds/src/components/FullPageOverlay/FullPageOverlay';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { SearchInput } from '~/eds/src/components/SearchInput/SearchInput';
import { Text } from '~/eds/src/components/Text/Text';
import { Filter } from '~/evifields/src/types/Filter';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useFormatNumber } from '~/i18n/hooks/useFormatNumber/useFormatNumber';
import { slices } from '~/redux/slices';
import { RoutePathType } from '~/routing/enums';
import { useRouting } from '~/routing/useRouting';
import { Nullable } from '~/types/Nullable';

import { MODEL_CART_WIDTH } from '../constants';
import {
  useGetLatestLibraryModelVersions,
  useGetModelLibrarySteps,
} from '../hooks';
import { Categories } from './Categories';
import { useModelLibraryCart } from './hooks';
import { ModelCard } from './ModelCard';
import { ModelCart } from './ModelCart';
import { ModelLibraryFilters } from './ModelLibraryFilters';
import { ModelPreview } from './ModelPreview';
import { getCategoryModels, testIsModelPublished } from './utils';

type Callback = () => void;

interface Props {
  onCancel: Callback;
  onNext: Callback;
}

export const ModelLibrary = ({ onCancel, onNext }: Props) => {
  const intl = useIntl();
  const formatNumber = useFormatNumber();
  const dispatch = useDispatch();
  const { navigate } = useRouting();
  const { toast } = useToast();

  const { addAllModelsToCart } = useModelLibraryCart(intl);

  const enableLibraryAdmin = useFlag(FlagType.DocumentXRayPrebuiltModelsAdmin);

  const isCartVisible = useSelector(
    slices.xRayLibrary.selectors.selectIsCartVisible,
  );

  const category = useSelector(slices.xRayLibrary.selectors.selectCategory);
  const [search, setSearch] = useState<Nullable<string>>(null);
  const initialFilters: Filter[] = [
    {
      id: uuid(),
      fieldId: 'modelState',
      operatorId: 'contains_any',
      values: [],
    },
  ];
  const [filters, setFilters] = useState<Filter[]>(initialFilters);

  const modelsInCart = useSelector(slices.xRayLibrary.selectors.selectCart);
  const cartStatusMessage = useSelector(
    slices.xRayLibrary.selectors.selectCartStatusMessage,
  );

  const { allModels, models, isLoading } = useGetLatestLibraryModelVersions({
    category,
    filters,
    search,
  });

  const modelLibrarySteps = useGetModelLibrarySteps(intl);

  const unpublishedModels = models.filter(
    (model) => !testIsModelPublished(model),
  );

  const modelsInCartInCategory = category
    ? modelsInCart.filter((model) =>
        model.tags.find((tag) => tag.label === category.label),
      )
    : modelsInCart;

  const modelsToAddCount =
    unpublishedModels.length - modelsInCartInCategory.length;

  const testIsDirty = (a: Filter[], b: Filter[]) => {
    const aValues = a.map((f) => f.values).flat();
    const bValues = b.map((f) => f.values).flat();
    return !isEqual(aValues, bValues);
  };

  const loadingContent = {
    isLoading,
    message: intl.formatMessage({
      id: 'ModelLibrary.loadingMessage',
      defaultMessage: 'Loading Model Library…',
      description: 'Loading message for model library',
    }),
  };

  const emptyContent =
    models.length === 0
      ? {
          message:
            search && category
              ? intl.formatMessage(
                  {
                    id: 'ModelLibrary.noModelsSearchResultsInCategory',
                    defaultMessage:
                      'No results found for "{search}" in the category "{categoryLabel}".',
                    description:
                      'Message when no models match search criteria in a category. Example: search = "contract terms", categoryText = "Legal"',
                  },
                  {
                    search,
                    categoryLabel: category.label,
                  },
                )
              : search
                ? intl.formatMessage(
                    {
                      id: 'ModelLibrary.noModelsSearchResults',
                      defaultMessage:
                        'No results found for "{search}" in All Categories.',
                      description:
                        'Message when no models match search criteria. Example: search = "contract terms"',
                    },
                    {
                      search,
                    },
                  )
                : filters
                  ? intl.formatMessage({
                      id: 'ModelLibrary.noModelsFound',
                      defaultMessage: 'No results found.',
                      description:
                        'Message when no models are found. Example: filterText = "sample text"',
                    })
                  : undefined,
          action:
            category && (search || testIsDirty(filters, initialFilters))
              ? {
                  mode: 'chip' as const,
                  status: 'active' as const,
                  text: intl.formatMessage({
                    id: 'ModelLibrary.searchAllCategories',
                    defaultMessage: 'Search All Categories',
                    description: 'Button text to search all categories.',
                  }),
                  onClick: () =>
                    dispatch(slices.xRayLibrary.actions.setCategory(null)),
                }
              : search || testIsDirty(filters, initialFilters)
                ? {
                    mode: 'chip' as const,
                    status: 'active' as const,
                    text: intl.formatMessage({
                      id: 'ModelLibrary.resetFilter',
                      defaultMessage: 'Clear Search',
                      description: 'Button text to reset filter',
                    }),
                    onClick: () => {
                      setFilters(initialFilters);
                      setSearch('');
                    },
                  }
                : undefined,
        }
      : undefined;

  const categorySubtext = !category ? (
    models.length === allModels.length ? (
      // All models in all categories
      <FormattedMessage
        id="ModelLibrary.categoryDescription.allModels"
        defaultMessage="Showing {modelsLocalize} {models, plural, one {model} other {models}}"
        description="Description for when all models in all categories are shown"
        values={{
          models: allModels.length,
          modelsLocalize: formatNumber(allModels.length),
        }}
      />
    ) : (
      // Some models in all categories
      <FormattedMessage
        id="ModelLibrary.categoryDescription.allCategories"
        defaultMessage="Showing {modelsCountLocalize} of {modelsTotalLocalize} {modelsTotal, plural, one {model} other {models}}"
        description="Description for the count of categories in view"
        values={{
          modelsTotal: allModels.length,
          modelsTotalLocalize: formatNumber(allModels.length),
          modelsCountLocalize: formatNumber(models.length),
        }}
      />
    )
  ) : models.length === getCategoryModels(category, allModels).length ? (
    // All models in a single category
    <FormattedMessage
      id="ModelLibrary.categoryDescription.singleCategory"
      defaultMessage="Showing {modelsLocalized} {models, plural, one {model} other {models}}"
      description="Description when all models in a single category are shown"
      values={{
        models: models.length,
        modelsLocalized: formatNumber(models.length),
      }}
    />
  ) : (
    // Some models in a single category
    <FormattedMessage
      id="ModelLibrary.categoryDescription.singleCategoryPartial"
      defaultMessage="Showing {modelsCount} of {modelsTotal} {modelsTotal, plural, one {model} other {models}}"
      description="Description when some models in a single category are shown"
      values={{
        modelsCount: `${models.length}`,
        modelsTotal: `${getCategoryModels(category, allModels).length}`,
      }}
    />
  );

  const tooltip =
    modelsInCart.length === 0
      ? intl.formatMessage({
          id: 'ModelLibrary.selectOneModel',
          defaultMessage: 'Select at least one model.',
          description: 'Tooltip when no models are selected',
        })
      : undefined;

  return (
    <FullPageOverlay
      actions={[
        {
          level: 'primary',
          text: intl.formatMessage({
            id: 'ModelLibrary.nextAddScope',
            defaultMessage: 'Next: Add Scope',
            description: 'Primary action button text',
          }),
          onClick: onNext,
          disabled: Boolean(tooltip),
          tooltip,
        },
      ]}
      backgroundGradient="library"
      cancelAction={{
        text: intl.formatMessage({
          id: 'ModelLibrary.close',
          defaultMessage: 'Close',
          description: 'Close button text',
        }),
        level: 'tertiary',
        onClick: onCancel,
      }}
      enableContentPadding={false}
      headerCalloutContent={
        <Layout spacing={4}>
          {enableLibraryAdmin && (
            <Actions
              actions={[
                {
                  icon: 'edit',
                  iconPosition: 'left',
                  level: 'secondary',
                  mode: 'chip',
                  text: intl.formatMessage({
                    id: 'ModelLibrary.adminManagement',
                    defaultMessage: 'Model Library Admin Management',
                    description: 'Button text for admin management',
                  }),
                  onClick: () => navigate(RoutePathType.XRayLibraryAdmin),
                },
              ]}
            />
          )}
          <Actions
            actions={[
              {
                count: modelsInCart.length,
                level: 'cta',
                mode: 'chip',
                text: intl.formatMessage({
                  id: 'ModelLibrary.viewSelectedModels',
                  defaultMessage: 'View Selected Models',
                  description:
                    'Button text showing selected models count. Example: count = 5',
                }),
                onClick: () => {
                  dispatch(slices.xRayLibrary.actions.toggleIsCartVisible());
                  if (isCartVisible) {
                    dispatch(
                      slices.xRayLibrary.actions.clearCartStatusMessage(),
                    );
                  }
                },
              },
            ]}
          />
        </Layout>
      }
      leftActions={[
        {
          icon: 'launch',
          iconPosition: 'left',
          text: intl.formatMessage({
            id: 'ModelLibrary.newBlankModel',
            defaultMessage: 'New blank model',
            description: 'Button text for creating new blank model',
          }),
          level: 'tertiary',
          onClick: () => navigate(`${RoutePathType.AutomationHubFields}/new`),
        },
      ]}
      loadingContent={loadingContent}
      panel={
        isCartVisible
          ? {
              cancelAction: {
                text: intl.formatMessage({
                  id: 'Panel.closeButton',
                  defaultMessage: 'Close',
                  description: 'Button label for closing the panel',
                }),
                onClick: () => {
                  dispatch(slices.xRayLibrary.actions.toggleIsCartVisible());
                  dispatch(slices.xRayLibrary.actions.clearCartStatusMessage());
                },
              },
              children: <ModelCart />,
              width: MODEL_CART_WIDTH,
              enableContentPadding: false,
              position: 'right',
              mode: 'inline',
              statusMessage: cartStatusMessage ?? undefined,
              title: intl.formatMessage({
                id: 'ModelCart.selectedModels',
                defaultMessage: 'Selected Models',
                description: 'Title for selected models panel',
              }),
            }
          : undefined
      }
      stepper={{
        currentStepId: 'model-library',
        isOrdered: true,
        steps: modelLibrarySteps,
      }}
      title={intl.formatMessage({
        id: 'ModelLibrary.title',
        defaultMessage: '1. Select from Model Library',
        description: 'Modal title for model selection step',
      })}
    >
      <Layout direction="column" overflowY="hidden" px={4} w="100%">
        <Layout preset="form-fields" py={6}>
          <SearchInput
            name="x-ray-model-library-search-input"
            placeholder={intl.formatMessage({
              id: 'ModelLibrary.searchPlaceholder',
              defaultMessage: 'Search models',
              description: 'Placeholder text for input to search models',
            })}
            value={search}
            onChange={setSearch}
          />
          <ModelLibraryFilters filters={filters} onChange={setFilters} />
        </Layout>
        <Layout spacing={4} align="flex-start">
          <Layout h="calc(100vh - 260px)">
            <Categories />
          </Layout>
          <Layout direction="column" flex="auto">
            <Layout
              align="center"
              justify="space-between"
              pb={emptyContent ? 16 : 2}
            >
              <Layout align="center" spacing={2}>
                <Text fontSize="16" variant="body-bold" lineHeight="48px">
                  {category?.label ??
                    intl.formatMessage({
                      id: 'ModelLibrary.allCategories',
                      defaultMessage: 'All Categories',
                      description: 'Title for all categories',
                    })}
                </Text>
                <Text>/</Text>
                <Text preset="help">{categorySubtext}</Text>
              </Layout>
              <Actions
                actions={
                  category
                    ? [
                        {
                          disabled:
                            models.length === 0 ||
                            unpublishedModels.length === 0 ||
                            modelsToAddCount <= 0,
                          tooltip:
                            models.length === 0
                              ? intl.formatMessage({
                                  id: 'ModelLibrary.noModelsToAdd',
                                  defaultMessage: 'No models to add.',
                                  description:
                                    'Tooltip when no models are available to add',
                                })
                              : unpublishedModels.length === 0
                                ? intl.formatMessage(
                                    {
                                      id: 'ModelLibrary.modelsAlreadyPublished',
                                      defaultMessage:
                                        '{count, plural, one {Model has} other {Models have}} already been published.',
                                      description:
                                        'Tooltip when models are already published. Example: count = 2',
                                    },
                                    {
                                      count: models.length,
                                    },
                                  )
                                : modelsToAddCount <= 0
                                  ? intl.formatMessage(
                                      {
                                        id: 'ModelLibrary.modelsAlreadyAdded',
                                        defaultMessage:
                                          '{count, plural, one {Model has} other {Models have}} been added.',
                                        description:
                                          'Tooltip when models have already been added to the shopping cart. Example: count = 2',
                                      },
                                      {
                                        count: models.length,
                                      },
                                    )
                                  : undefined,
                          level: 'tertiary-active',
                          mode: 'link',
                          text: intl.formatMessage({
                            id: 'ModelLibrary.addAllModels',
                            defaultMessage: 'Add All Models',
                            description: 'Button text to add all models',
                          }),
                          onClick: () => {
                            if (!category) {
                              toast({
                                message: intl.formatMessage({
                                  id: 'ModelLibrary.selectCategory',
                                  defaultMessage: 'Select a category.',
                                  description:
                                    'Warning message to select a category',
                                }),
                                status: 'warning',
                              });
                            } else {
                              addAllModelsToCart({
                                models: unpublishedModels,
                                category,
                                count: modelsToAddCount,
                              });
                            }
                          },
                        },
                      ]
                    : []
                }
              />
            </Layout>

            <Layout maxH="calc(100vh - 316px)" overflowY="auto" w="100%">
              <ContentContainer placeholderContent={emptyContent}>
                <CardsGrid
                  cards={models}
                  renderItem={(model) => (
                    <ModelCard model={model} search={search ?? undefined} />
                  )}
                  gridOptions={{
                    columns: isCartVisible ? 2 : 3,
                    columnWidths: isCartVisible
                      ? ['48%', '48%']
                      : ['32%', '32%', '32%'],
                    width: '100%',
                  }}
                />
              </ContentContainer>
            </Layout>
          </Layout>
        </Layout>
      </Layout>
      <ModelPreview categoryModels={models} />
    </FullPageOverlay>
  );
};
